import {
  AuditClient,
  AttentionClient,
  CalendarAiClient,
  DocumentsClient,
  EvidenceClient,
  ExceptionGovernanceClient,
  FrameworksClient,
  GovernanceClient,
  PolicyAiClient,
  ProposalAiClient,
  RegulatoryClient,
  StudioClient,
  SopAiClient,
  VendorAiClient,
  ApiError,
  MeetingAiClient,
} from "@odingard/sdk-client";

const TOKEN_KEY = "ods.session.token";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore storage errors */
  }
}

const BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "http://localhost:8787";

let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  unauthorizedHandler = fn;
}

export const api = new StudioClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const proposalApi = new ProposalAiClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const policyApi = new PolicyAiClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const governanceApi = new GovernanceClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const frameworksApi = new FrameworksClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const evidenceApi = new EvidenceClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const exceptionsApi = new ExceptionGovernanceClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const auditApi = new AuditClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const regulatoryApi = new RegulatoryClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const documentsApi = new DocumentsClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const calendarApi = new CalendarAiClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const vendorApi = new VendorAiClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});
export const meetingApi = new MeetingAiClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export interface ReviewScopeItem {
  id: string;
  documentId: string;
  version: number;
  checksum: string;
  page?: number | null;
  characterStart?: number | null;
  characterEnd?: number | null;
  section?: string | null;
}

export interface ReviewLens {
  id: string;
  reviewId: string;
  lensKey: string;
  assignedReviewerId: string | null;
  state: string;
  required: boolean;
  dueAt: string | null;
  revision: number;
}

export interface ReviewRecord {
  id: string;
  organizationId: string;
  purpose: string;
  lifecycleState: string;
  dueAt: string;
  requesterUserId: string;
  ownerUserId: string;
  revision: number;
  criteriaSetId: string | null;
  criteriaSetVersion: number | null;
  scopeItems: ReviewScopeItem[];
  lenses: ReviewLens[];
  createdAt: string;
  updatedAt: string;
}

export interface ReviewFinding {
  id: string;
  reviewId: string;
  lensId: string;
  criterionId: string | null;
  severity: string;
  state: string;
  title: string;
  description: string;
  location: {
    documentId: string;
    version: number;
    checksum: string;
    page?: number | null;
    characterStart?: number | null;
    characterEnd?: number | null;
    section?: string | null;
  } | null;
  revision: number;
  raisedBy?: string;
}

export interface ReviewConclusion {
  id: string;
  reviewId: string;
  lensId: string;
  value: string;
  rationale: string;
  concludedBy: string;
  concludedAt: string;
  revision: number;
}

export interface ReviewOutcome {
  id: string;
  reviewId: string;
  outcome: string;
  rationale: string;
  recordedBy: string;
  recordedAt: string;
  revision: number;
}

export interface ReviewWorkItem {
  id: string;
  type: "review.lens_assigned" | "review.lens_overdue" | "review.awaiting_outcome";
  reviewId: string;
  lensId?: string;
  title: string;
  assignedTo: string;
  dueAt: string | null;
  sourceRevision: number;
}

export interface ReviewTransition {
  id: string;
  fromState: string;
  toState: string;
  reason: string;
  actorId: string;
  createdAt: string;
}

export interface ReviewCriterion {
  id: string;
  code: string;
  criterionText: string;
  blockingSeverity: string | null;
  position: number;
}

export interface ReviewCriteriaSet {
  id: string;
  name: string;
  version: number;
  state: string;
  blockingSeverity: string;
  criteria: ReviewCriterion[];
}

function reviewPath(orgId: string, suffix: string): string {
  return `/organizations/${encodeURIComponent(orgId)}/review-ai${suffix}`;
}

async function reviewRequest<T>(
  orgId: string,
  suffix: string,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
    ...(init.headers as Record<string, string> | undefined),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${reviewPath(orgId, suffix)}`, { ...init, headers });
  } catch {
    throw new Error("Unable to reach Review AI.");
  }
  const text = await response.text();
  const payload = text ? (JSON.parse(text) as { error?: { code?: string; message?: string; details?: Record<string, unknown> } }) : {};
  if (!response.ok) {
    const error = payload.error;
    throw new ApiError(
      response.status,
      error?.code ?? "error",
      error?.message ?? `Review AI request failed (${response.status}).`,
      error?.details,
    );
  }
  return payload as T;
}

export const reviewApi = {
  listReviews: (orgId: string) => reviewRequest<{ reviews: ReviewRecord[] }>(orgId, "/reviews"),
  createReview: (orgId: string, body: { purpose: string; ownerUserId: string; dueAt: string; criteriaSetId: string }) =>
    reviewRequest<{ review: ReviewRecord }>(orgId, "/reviews", { method: "POST", body: JSON.stringify(body) }),
  listCriteriaSets: (orgId: string) =>
    reviewRequest<{ criteriaSets: ReviewCriteriaSet[] }>(orgId, "/criteria-sets"),
  getCriteriaSet: (orgId: string, criteriaSetId: string) =>
    reviewRequest<{ criteriaSet: ReviewCriteriaSet }>(orgId, `/criteria-sets/${encodeURIComponent(criteriaSetId)}`),
  createCriteriaSet: (orgId: string, body: { name: string; blockingSeverity: string }) =>
    reviewRequest<{ criteriaSet: ReviewCriteriaSet }>(orgId, "/criteria-sets", { method: "POST", body: JSON.stringify(body) }),
  addCriterion: (orgId: string, criteriaSetId: string, body: { code: string; criterionText: string; blockingSeverity: string | null; position: number }) =>
    reviewRequest<{ criterion: ReviewCriterion }>(orgId, `/criteria-sets/${encodeURIComponent(criteriaSetId)}/criteria`, { method: "POST", body: JSON.stringify(body) }),
  publishCriteriaSet: (orgId: string, criteriaSetId: string) =>
    reviewRequest<{ criteriaSet: ReviewCriteriaSet }>(orgId, `/criteria-sets/${encodeURIComponent(criteriaSetId)}/publish`, { method: "POST" }),
  getReview: (orgId: string, reviewId: string) =>
    reviewRequest<{ review: ReviewRecord }>(orgId, `/reviews/${encodeURIComponent(reviewId)}`),
  listFindings: (orgId: string, reviewId: string) =>
    reviewRequest<{ findings: ReviewFinding[] }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/findings`,
    ),
  actionableLenses: (orgId: string) =>
    reviewRequest<{ lenses: ReviewLens[] }>(orgId, "/lenses/actionable"),
  getOutcome: (orgId: string, reviewId: string) =>
    reviewRequest<{ outcome: ReviewOutcome | null }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/outcome`,
    ),
  conclusionHistory: (orgId: string, reviewId: string, lensId: string) =>
    reviewRequest<{ conclusions: ReviewConclusion[] }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/lenses/${encodeURIComponent(lensId)}/conclusion-history`,
    ),
  listTransitions: (orgId: string, reviewId: string) =>
    reviewRequest<{ transitions: ReviewTransition[] }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/transitions`,
    ),
  concludeLens: (orgId: string, reviewId: string, lensId: string, body: { outcome: string; rationale: string }) =>
    reviewRequest<{ conclusion: ReviewConclusion }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/lenses/${encodeURIComponent(lensId)}/conclusion`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  reopenLens: (orgId: string, reviewId: string, lensId: string, body: { reason: string }) =>
    reviewRequest<{ lens: ReviewLens }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/lenses/${encodeURIComponent(lensId)}/reopen`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  recordOutcome: (orgId: string, reviewId: string, body: { outcome: string; rationale: string }) =>
    reviewRequest<{ outcome: ReviewOutcome }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/outcome`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  transition: (orgId: string, reviewId: string, body: { toState: string; reason: string }) =>
    reviewRequest<{ review: ReviewRecord }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/transitions`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  addScopeItem: (orgId: string, reviewId: string, body: { documentId: string; version: number }) =>
    reviewRequest<{ scopeItem: ReviewScopeItem }>(orgId, `/reviews/${encodeURIComponent(reviewId)}/scope-items`, { method: "POST", body: JSON.stringify(body) }),
  assignLens: (orgId: string, reviewId: string, body: { lensKey: string; required: boolean; reviewerId: string }) =>
    reviewRequest<{ lens: ReviewLens }>(orgId, `/reviews/${encodeURIComponent(reviewId)}/lenses`, { method: "POST", body: JSON.stringify(body) }),
  reassignLens: (orgId: string, reviewId: string, lensId: string, body: { reviewerId: string; reason: string }) =>
    reviewRequest<{ lens: ReviewLens }>(orgId, `/reviews/${encodeURIComponent(reviewId)}/lenses/${encodeURIComponent(lensId)}/assign`, { method: "POST", body: JSON.stringify(body) }),
  raiseFinding: (
    orgId: string,
    reviewId: string,
    body: {
      lensId: string;
      criterionId: string;
      severity: string;
      summary: string;
      detail: string | null;
      anchors: Array<{
        scopeItemId: string;
        anchorKind: "PAGE" | "OFFSET" | "SECTION";
        pageNumber?: number | null;
        offsetStart?: number | null;
        offsetEnd?: number | null;
        sectionPath?: string | null;
      }>;
    },
  ) =>
    reviewRequest<{ finding: ReviewFinding }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/findings`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  correctFinding: (
    orgId: string,
    reviewId: string,
    findingId: string,
    body: { summary: string; detail: string | null },
  ) =>
    reviewRequest<{ finding: ReviewFinding }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/findings/${encodeURIComponent(findingId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  withdrawFinding: (orgId: string, reviewId: string, findingId: string, reason: string) =>
    reviewRequest<{ finding: ReviewFinding }>(
      orgId,
      `/reviews/${encodeURIComponent(reviewId)}/findings/${encodeURIComponent(findingId)}/withdraw`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
};
export const sopApi = new SopAiClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

export const attentionApi = new AttentionClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

// The shell reads attention and context on every page without the person
// asking. An ambient read is not authority over the session: a refusal here
// degrades the indicator, and the next authoritative call is what decides the
// session has ended.
export const shellAttentionApi = new AttentionClient({
  baseUrl: BASE_URL,
  getToken,
});

export { BASE_URL };
